<template>
    <BaseDialog
        title="Bulk Upload Testimonials via Google Drive"
        :visible="visible"
        :size="DialogSize.medium"
        :okLabel="allDone ? 'Close' : loading ? 'Uploading' : 'Start Upload'"
        :saveAction="startBulkUpload"
        :closeAction="closeDialog"
        :loading="loading"
    >
        <template #content>
            <div class="flex flex-col gap-2">
                <FormItem itemLabel="Products" class="mb-3">
                    <template #content>
                        <OptionsMultiSelection
                            id="productSelect"
                            optionType="product"
                            :selection="selectedProducts"
                            @update:selection="onProductsSelected"
                        />
                    </template>
                </FormItem>
                <FormItem
                    itemLabel="Google Drive Folder ID or Link"
                    :isInvalid="driveFolderIdError"
                    :errorMessage="driveFolderIdErrorMessage"
                >
                    <template #content>
                        <InputText
                            id="driveFolderId"
                            v-model="driveFolderId"
                            class="w-full"
                            :class="{ 'p-invalid': driveFolderIdError }"
                            autocomplete="off"
                        />
                    </template>
                </FormItem>
                <div>
                    <Checkbox inputId="ignoreSubFolders" v-model="ignoreSubFolders" binary="true" />
                    <label for="ignoreSubFolders" class="ml-2"> Include subfolders </label>
                </div>
            </div>
            <div v-if="videoList.length > 0" class="mt-6">
                <h4 class="mb-2">Upload Progress</h4>
                <div class="mb-4">
                    <ProgressBar
                        :value="progressPercent"
                        :showValue="true"
                        style="height: 20px"
                        :displayValueTemplate="progressPercent + '%'"
                    />
                    <div class="text-xs mt-1">
                        {{ completedCount }} of {{ videoList.length }} videos finished
                        <span v-if="allDone && !hasErrors" class="text-green-600 ml-2">All uploads complete!</span>
                        <span v-if="allDone && hasErrors" class="text-red-600 ml-2">Some uploads failed.</span>
                    </div>
                </div>
                <DataTable :value="videoList" class="w-full">
                    <Column field="name" header="Video Name"></Column>
                    <Column header="Status">
                        <template #body="slotProps">
                            <div v-if="slotProps.data.status === 'review'">
                                <Tag severity="success" value="Uploaded" />
                            </div>
                            <div v-else>
                                <ProgressBar mode="indeterminate" style="height: 8px" />
                                <span class="ml-2 text-secondary">Uploading...</span>
                            </div>
                        </template>
                    </Column>
                </DataTable>
            </div>
        </template>
    </BaseDialog>
</template>

<script setup>
import BaseDialog from "@/shared/components/BaseDialog.vue";
import Checkbox from "primevue/checkbox";
import { ref, defineEmits, computed, inject, onBeforeUnmount, defineModel } from "vue";
import { DialogSize } from "@/core/enums/DialogSizeEnum.js";
import { InputText } from "primevue";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import ProgressBar from "primevue/progressbar";
import Tag from "primevue/tag";
import { useToast } from "primevue/usetoast";
import OptionsMultiSelection from "@/shared/common/OptionsMultiSelection.vue";
import FormItem from "@/shared/components/FormItem.vue";

const visible = defineModel("visible");

const testimonialService = inject("testimonialService");

const driveFolderId = ref("");
const loading = ref(false);
const ignoreSubFolders = ref(false);
const videoList = ref([]);
const selectedProducts = ref([]);
const toast = useToast();

let timeoutId = null;

const completedCount = computed(() => {
    return videoList.value.filter((video) => video.status == "review").length;
});

const allDone = computed(() => {
    if (!videoList.value.length) {
        return false;
    }

    return completedCount.value == videoList.value.length;
});

const progressPercent = computed(() =>
    videoList.value.length === 0 ? 0 : Math.round((completedCount.value / videoList.value.length) * 100),
);

const startBulkUpload = async () => {
    if (allDone.value) {
        visible.value = false;
        return;
    }

    try {
        loading.value = true;

        const response = await testimonialService.googleDriveBulkUpload({
            driveFolderId: driveFolderId.value,
            ignoreSubFolders: ignoreSubFolders.value,
            products: selectedProducts.value.map((p) => p.value),
        });

        videoList.value = response.videos.map((video) => ({
            ...video,
            status: "pending",
        }));

        if (videoList.value.length > 0) {
            pollUploadStatus();
        }
    } catch (e) {
        toast.add({ severity: "error", summary: "Error", detail: "Failed to start bulk upload", life: 4000 });
        loading.value = false;
    }
};

const pollUploadStatus = async () => {
    const videoIds = videoList.value.map((video) => video.id);

    try {
        const response = await testimonialService.googleDriveBulkUploadStatus({ videoIds });

        response.statuses.forEach((video) => {
            const index = videoList.value.findIndex((localVideo) => localVideo.id == video.videoId);

            if (index < 0) {
                return;
            }

            videoList.value[index].status = video.status;
        });

        if (!allDone.value) {
            timeoutId = setTimeout(pollUploadStatus, 4000);
        } else {
            loading.value = false;
        }
    } catch (e) {}
};

const onProductsSelected = (products) => {
    selectedProducts.value = products;
};

const closeDialog = () => {
    if (loading.value && !allDone.value) {
        toast.add({
            severity: "info",
            summary: "Upload in Progress",
            detail: "The upload process will continue in the background.",
            life: 4000,
        });
    }
    visible.value = false;
};

onBeforeUnmount(() => {
    if (timeoutId) {
        clearTimeout(timeoutId);
    }
});
</script>
